/// <amd-module name="Wit/BacklogsHub/BacklogsHeader/Components/BacklogsHeader" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { IBacklogsDirectoryService } from "Wit/BacklogsHub/Common/Services/IBacklogsDirectoryService";
import * as Resources from "Wit/BacklogsHub/Resources";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { DirectoryDropdown } from "Wit/DirectoryView/Components/DirectoryDropdown";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface IBacklogsHeaderProps {
    commandBarItems?: IHeaderCommandBarItem[];
}

export class BacklogsHeader extends TfsComponent<IBacklogsHeaderProps, {}> {
    private itemProvider: DirectoryDropdownProvider;
    private buttonCount: ObservableValue<number> = new ObservableValue(3);
    private timerManagement: TimerManagement;
    private debouncedButtonCount: () => void;
    private isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, "WebPlatform.WorkItemTracking.NewSprintsBacklogsView", false);

    constructor(props: IBacklogsHeaderProps, context: IVssComponentContext) {
        super(props, context);

        const routeValues: { [key: string]: string } = {};
        if (this.projectData?.id) {
            routeValues["project"] = this.projectData.id;
        }

        const directoryHref = routeUrl(this.context.pageContext, "ms.vss-work-web.new-backlogs-directory-route", routeValues);
        const directoryService = context.pageContext.getService<IBacklogsDirectoryService>("IBacklogsDirectoryService");
        this.itemProvider = new DirectoryDropdownProvider({
            directoryService,
            artifactTypeName: Resources.Backlogs,
            iconProps: { iconName: "BacklogList", ariaHidden: true },
            directoryHref,
            favoriteType: FavoriteTypes.WORK_TEAMBACKLOG,
            newSprintsViewEnabled: this.isNewSprintsViewEnabled,
        });

        if (this.isNewSprintsViewEnabled) {
            this.timerManagement = new TimerManagement();
            this.debouncedButtonCount = this.timerManagement.debounce(this.updateButtonCount, 300, { trailing: true });
        }
    }

    public componentDidMount() {
        if (this.isNewSprintsViewEnabled) {
            window.addEventListener('resize', this.debouncedButtonCount);
            this.buttonCount.value = 3;
            this.updateButtonCount();
        }
    }

    public componentWillUnmount() {
        if (this.isNewSprintsViewEnabled) {
            window.removeEventListener('resize', this.debouncedButtonCount);
            this.timerManagement.dispose();
        }
    }

    public render(): JSX.Element | null {
        const currentDirectoryService = this.context.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        const currentItem = currentDirectoryService.getCurrentDirectory().value;

        return this.isNewSprintsViewEnabled ? (
            <Observer
                buttonCount={{
                    observableValue: this.buttonCount
                }}
            >
                {(props) => (
                    <Header
                        buttonCount={props.buttonCount}
                        className="wit-backlogs-header-row sprints-view-ff-enabled"
                        title={
                            <DirectoryDropdown
                                itemProvider={this.itemProvider}
                                selectedItem={currentItem}
                                onSelect={(event, item) => currentDirectoryService.setCurrentDirectory(item.data?.directory)}
                            />
                        }
                        commandBarClassName="flex flex-end flex-shrink"
                        commandBarItems={this.props.commandBarItems}
                        titleSize={props.buttonCount === 3 ? TitleSize.Large : TitleSize.Small}
                    />
                )}
            </Observer>
        ) : (
            <Header
                className="wit-backlogs-header-row"
                title={
                    <DirectoryDropdown
                        itemProvider={this.itemProvider}
                        selectedItem={currentItem}
                        onSelect={(event, item) => currentDirectoryService.setCurrentDirectory(item.data?.directory)}
                    />
                }
                titleSize={TitleSize.Large}
            />
        );
    }

    private updateButtonCount = () => {
        this.buttonCount.value = window.matchMedia('(max-width: 600px)').matches ? 0 : 3;
    }
}
